import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'

interface Account {
  id: number
  name: string
  slug: string
}

interface Customer {
  id: number
  name: string
  email: string
  phone: string
  tax_id: string
  city: string
  is_wholesale: boolean
}

interface Props {
  customers: Customer[]
  account: Account
  permissions?: {
    can_manage_customers: boolean
  }
  can_manage?: boolean
  can_delete?: boolean
}

export default function CustomersIndex({ customers, account, permissions, can_manage, can_delete }: Props) {
  const [search, setSearch] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const handleSearch = (value: string) => {
    setSearch(value)
    router.get(`/${account.slug}/customers`, { search: value }, { preserveState: true, replace: true })
  }

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`¿Estás seguro de eliminar a "${name}"?`)) return
    setDeletingId(id)
    router.delete(`/${account.slug}/customers/${id}`, {
      preserveScroll: true,
      onFinish: () => setDeletingId(null),
    })
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]">Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">
            {customers.length} {customers.length === 1 ? 'cliente' : 'clientes'}
          </p>
        </div>
        {can_manage && (
          <Link
            href={`/${account.slug}/customers/new`}
            className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded hover:bg-[#e6ad06] transition-colors font-semibold"
          >
            + Nuevo Cliente
          </Link>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <input
          type="text"
          placeholder="Buscar cliente por nombre..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
        />
      </div>

      {customers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg mb-2">No hay clientes</p>
          {search ? (
            <p className="text-sm text-gray-400">
              Intenta con otro nombre o{' '}
              <button
                onClick={() => handleSearch('')}
                className="text-[#55ACEE] hover:underline"
              >
                limpia la búsqueda
              </button>
            </p>
          ) : can_manage ? (
            <Link
              href={`/${account.slug}/customers/new`}
              className="inline-block mt-3 text-[#55ACEE] hover:underline font-medium"
            >
              Crear el primero
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {customers.map((customer) => (
              <div key={customer.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#202020] truncate">
                      {customer.name}
                      {customer.is_wholesale && (
                        <span className="ml-2 inline-block bg-yellow-200 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Mayorista
                        </span>
                      )}
                    </p>
                    {customer.email && (
                      <p className="text-sm text-gray-500 truncate">{customer.email}</p>
                    )}
                    {customer.phone && (
                      <p className="text-sm text-gray-500">{customer.phone}</p>
                    )}
                    {customer.tax_id && (
                      <p className="text-xs text-gray-400 font-mono">RTN: {customer.tax_id}</p>
                    )}
                    {customer.city && (
                      <p className="text-xs text-gray-400">{customer.city}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  {can_manage && (
                    <Link
                      href={`/${account.slug}/customers/${customer.id}/edit`}
                      className="text-[#55ACEE] hover:underline text-sm font-medium"
                    >
                      Editar
                    </Link>
                  )}
                  {can_delete && (
                    <button
                      onClick={() => handleDelete(customer.id, customer.name)}
                      disabled={deletingId === customer.id}
                      className="text-red-600 hover:underline text-sm font-medium disabled:opacity-50"
                    >
                      {deletingId === customer.id ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabla */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RTN</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ciudad</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-[#202020]">
                      {customer.name}
                      {customer.is_wholesale && (
                        <span className="ml-2 inline-block bg-yellow-200 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Mayorista
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-500">{customer.email || '—'}</td>
                    <td className="px-6 py-4 text-gray-500">{customer.phone || '—'}</td>
                    <td className="px-6 py-4 text-gray-500 font-mono text-sm">{customer.tax_id || '—'}</td>
                    <td className="px-6 py-4 text-gray-500">{customer.city || '—'}</td>
                    <td className="px-6 py-4 text-right space-x-3">
                      {can_manage && (
                        <Link
                          href={`/${account.slug}/customers/${customer.id}/edit`}
                          className="text-[#55ACEE] hover:underline"
                        >
                          Editar
                        </Link>
                      )}
                      {can_delete && (
                        <button
                          onClick={() => handleDelete(customer.id, customer.name)}
                          disabled={deletingId === customer.id}
                          className="text-red-600 hover:underline disabled:opacity-50"
                        >
                          {deletingId === customer.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  )
}
